import { useState, useEffect, useCallback } from 'react';
import { Message } from '@/types';
/**
 * useThinkingCollapse Hook
 * 管理思考区域和工具调用的折叠状态
 */
export function useThinkingCollapse(message: Message) {
  // Determine if thinking section should be collapsed by default
  const hasTextContent =
    message.choices?.[0]?.blocks?.some(block => block.type === 'text') || false;
  const [isThinkingCollapsed, setIsThinkingCollapsed] = useState<
    Map<number, boolean>
  >(new Map());
  const [isToolCollapsed, setIsToolCollapsed] = useState<Set<string>>(
    new Set<string>()
  );
  const [hasAutoCollapsed, setHasAutoCollapsed] = useState<boolean>(false);
  /**
   * Toggle thinking section collapse by group index
   */
  const toggleThinkingCollapse = useCallback((groupIndex: number) => {
    setIsThinkingCollapsed(prev => {
      const newMap = new Map(prev);
      const currentState = newMap.get(groupIndex) ?? true;
      newMap.set(groupIndex, !currentState);
      return newMap;
    });
  }, []);
  /**
   * Toggle individual tool call/result collapse
   */
  const toggleToolCollapse = useCallback((toolId: string) => {
    setIsToolCollapsed(prev => {
      const newSet = new Set(prev);
      if (newSet.has(toolId)) {
        newSet.delete(toolId);
      } else {
        newSet.add(toolId);
      }
      return newSet;
    });
  }, []);
  /**
   * Reset state when message ID changes (handle message switching)
   */
  useEffect(() => {
    setIsThinkingCollapsed(new Map()); // Reset all collapsed states
    setHasAutoCollapsed(false);
  }, [message.id]);
  /**
   * Auto-collapse thinking section when text content first appears
   */
  useEffect(() => {
    if (hasTextContent && !hasAutoCollapsed) {
      // Auto-collapse all thinking sections when text content first appears
      setIsThinkingCollapsed(new Map());
      setHasAutoCollapsed(true);
    }
  }, [hasTextContent, hasAutoCollapsed]);
  return {
    isThinkingCollapsed,
    isToolCollapsed,
    toggleThinkingCollapse,
    toggleToolCollapse,
  };
}
